/**
 * The HTTP client.
 *
 * Two things here are deliberate and easy to get wrong.
 *
 * The access token lives in a module variable, never in localStorage or sessionStorage. Anything
 * in web storage is readable by every script on the page, so a single XSS flaw would hand over
 * the session. A variable dies with the tab; the refresh cookie is HttpOnly and restores the
 * session on reload.
 *
 * Refresh is single-flight. When a page fires six queries at once and the token has just expired,
 * all six get a 401 together. Refreshing per request would rotate the refresh token six times
 * concurrently — and since rotation is single-use by design, five of those would look like token
 * reuse and the backend would revoke the whole chain, logging the user out for doing nothing
 * wrong. One shared promise avoids that entirely.
 */

export interface ProblemDetail {
  status: number;
  title?: string;
  detail?: string;
  code?: string;
  traceId?: string;
  details?: { fields?: Record<string, string> } & Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string>;
  readonly traceId?: string;

  constructor(status: number, problem?: ProblemDetail) {
    super(problem?.detail ?? problem?.title ?? "Something went wrong.");
    this.name = "ApiError";
    this.status = status;
    this.code = problem?.code ?? "UNKNOWN";
    this.fieldErrors = problem?.details?.fields ?? {};
    this.traceId = problem?.traceId;
  }

  /** A conflict the user can resolve by reloading — shown differently from a hard failure. */
  get isConcurrentModification(): boolean {
    return this.code === "CONCURRENT_MODIFICATION";
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

/** Shared across concurrent 401s so only one refresh is ever in flight. */
let refreshInFlight: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Called when the session cannot be recovered, so the app can redirect to sign-in. */
export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

async function readProblem(response: Response): Promise<ProblemDetail | undefined> {
  try {
    return (await response.json()) as ProblemDetail;
  } catch {
    // A gateway or proxy error may not be JSON at all. The status still tells us enough.
    return undefined;
  }
}

/** What a successful refresh yields. Null means the session could not be recovered. */
export interface RefreshedSession {
  accessToken: string;
  expiresAt: string;
}

let lastRefresh: RefreshedSession | null = null;

async function performRefresh(): Promise<boolean> {
  const response = await fetch("/api/v1/auth/refresh", {
    method: "POST",
    // Required for the HttpOnly refresh cookie to be sent at all.
    credentials: "include",
  });

  if (!response.ok) {
    accessToken = null;
    lastRefresh = null;
    return false;
  }

  const body = (await response.json()) as RefreshedSession;
  accessToken = body.accessToken;
  lastRefresh = body;
  return true;
}

function refreshOnce(): Promise<boolean> {
  // The first caller starts the refresh; everyone else awaits the same promise.
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

/**
 * The single refresh path for the whole application.
 *
 * <p>Everything that needs a new access token — the 401 retry below, session recovery on load,
 * and the proactive timer — goes through here, so only one refresh is ever in flight.
 *
 * <p>That matters more than it looks. Refresh tokens rotate and are single-use by design, so two
 * concurrent refreshes mean the second presents a token the first already consumed. The server
 * cannot distinguish that from a stolen token being replayed, and correctly revokes the entire
 * chain — signing the user out for doing nothing worse than opening two tabs, or for React
 * StrictMode mounting an effect twice. An earlier version had a second refresh path in
 * AuthContext and did exactly this.
 *
 * @returns the new token and its expiry, or null if the session could not be recovered
 */
export async function refreshSession(): Promise<RefreshedSession | null> {
  const recovered = await refreshOnce();
  return recovered ? lastRefresh : null;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /**
   * JSON-encoded, unless it is {@link FormData} — the browser has to set that content type
   * itself, because only it knows the multipart boundary it generated.
   */
  body?: unknown;
  /** Set on requests that must not be retried after a refresh — the auth endpoints themselves. */
  skipAuthRetry?: boolean;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, skipAuthRetry = false, signal } = options;

  const isMultipart = body instanceof FormData;

  const send = (): Promise<Response> =>
    fetch(path, {
      method,
      credentials: "include",
      headers: {
        // Deliberately absent for multipart: setting it by hand omits the boundary and the
        // server cannot then parse a single part of the request.
        ...(body !== undefined && !isMultipart
          ? { "Content-Type": "application/json" }
          : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(body !== undefined
        ? { body: isMultipart ? (body as FormData) : JSON.stringify(body) }
        : {}),
      ...(signal ? { signal } : {}),
    });

  let response = await send();

  if (response.status === 401 && !skipAuthRetry) {
    const recovered = await refreshOnce();
    if (recovered) {
      response = await send();
    } else {
      onSessionLost?.();
      throw new ApiError(401, await readProblem(response));
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readProblem(response));
  }

  // 204 and 202 carry no body; parsing one would throw on an empty stream.
  if (response.status === 204 || response.headers.get("Content-Length") === "0") {
    return undefined as T;
  }

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * Fetches a file and hands it to the browser to save.
 *
 * <p>A plain link cannot be used: the access token lives in memory and travels in a header, and
 * an anchor sends neither. So the bytes are fetched like any other request and turned into a
 * short-lived object URL, which is revoked immediately afterwards — leaving it alive would pin
 * the whole file in memory for as long as the tab is open.
 */
async function download(path: string, fileName: string): Promise<void> {
  const response = await fetch(path, {
    credentials: "include",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (!response.ok) {
    throw new ApiError(response.status, await readProblem(response));
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  /* Revoked on the next turn rather than immediately: some browsers have not yet started
     reading the blob when click() returns, and revoking under them cancels the download. */
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Opens a file in a new tab, when it is a kind a browser can show.
 *
 * <p>The tab is opened first, before anything is awaited, and that ordering is the whole trick. A
 * browser allows a new tab only while the click that asked for it is still being handled; a tab
 * opened after an `await` is an unrequested popup, and is blocked. So an empty tab is taken now,
 * inside the click, and pointed at the file once it has arrived.
 *
 * <p>The server has the last word on whether a file opens at all. It inspects the bytes, and a file
 * that is not what it claims comes back as a plain octet stream — in which case the tab is closed
 * and the file is downloaded instead, rather than leaving an empty tab behind.
 */
async function view(path: string, fileName: string): Promise<void> {
  const tab = window.open("", "_blank");
  if (!tab) {
    throw new ApiError(0, {
      detail: "The browser blocked the new tab. Allow pop-ups for this site, or download the file.",
    } as ProblemDetail);
  }
  // Severed at once: the tab will only ever hold a file, and has no business reaching back into
  // the page that opened it.
  tab.opener = null;
  tab.document.title = fileName;
  tab.document.body.textContent = `Opening ${fileName}…`;

  try {
    const response = await fetch(`${path}?inline=true`, {
      credentials: "include",
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, await readProblem(response));
    }

    const blob = await response.blob();
    if (blob.type === "" || blob.type === "application/octet-stream") {
      tab.close();
      await download(path, fileName);
      return;
    }

    const url = URL.createObjectURL(blob);
    tab.location.href = url;
    /* Kept alive rather than revoked at once: the new tab is still reading it, and a PDF viewer
       fetches pages lazily. A minute is ample, and frees the memory well before the tab is gone. */
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (caught) {
    tab.close();
    throw caught;
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
  download,
  view,
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
